import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, share, tap } from 'rxjs/operators';
import { CacheStatsService, HttpCacheService } from '../services/cache.service';

interface CacheConfig {
  ttl: number;
  staleTtl?: number;
  bypass?: boolean;
}

const defaultCacheConfig: CacheConfig = {
  ttl: 60000,
  staleTtl: 300000,
};

const urlCacheConfigs: ReadonlyMap<string, CacheConfig> = new Map<string, CacheConfig>([
  ['/api/connections', { ttl: 0, bypass: true }],
  ['/connections', { ttl: 0, bypass: true }],
  ['/api/skills', { ttl: 86400000, staleTtl: 172800000 }],
  ['/api/industries', { ttl: 86400000, staleTtl: 172800000 }],
  ['/api/categories', { ttl: 86400000, staleTtl: 172800000 }],
  ['/api/brands/discover', { ttl: 300000 }],
  ['/api/creators/discover', { ttl: 300000 }],
  ['/api/creators/listing/paginated', { ttl: 30000, staleTtl: 120000 }],
  ['/api/jobs/listing/paginated', { ttl: 30000, staleTtl: 120000 }],
  ['/api/jobs/listing/featured', { ttl: 30000, staleTtl: 60000 }],
  ['/api/listings/public', { ttl: 120000 }],
  ['/api/brands/profile', { ttl: 60000 }],
  ['/api/creators/profile', { ttl: 60000 }],
]);

/**
 * De-duplicates concurrent identical GETs.
 *
 * Keys are METHOD:url with no user identity, so this map MUST be cleared on logout:
 * otherwise, if user A signs out and user B signs in on the same tab while a request
 * such as GET /api/creators/profile is still in flight, user B's identical request
 * would be served user A's response. See the 'app:logout' listener below.
 */
const inFlightRequests = new Map<string, Observable<unknown>>();

const backgroundTimers = new Set<ReturnType<typeof setTimeout>>();

/**
 * Drops all cross-user request state. Bound to the 'app:logout' event that
 * AuthService dispatches on logout and account deletion.
 */
export function clearInFlightRequests(): void {
  inFlightRequests.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('app:logout', () => {
    clearInFlightRequests();
    clearBackgroundTimers();
  });
}

function getCacheConfig(url: string): CacheConfig {
  for (const [pattern, config] of urlCacheConfigs) {
    if (url.includes(pattern)) {
      return config;
    }
  }
  return defaultCacheConfig;
}

function getCacheKey(req: HttpRequest<unknown>): string {
  const auth = req.headers.get('Authorization');
  if (auth) {
    const tokenSuffix = auth.length > 20 ? auth.slice(-16) : auth;
    return `${req.method}:${req.urlWithParams}:${tokenSuffix}`;
  }
  return `${req.method}:${req.urlWithParams}`;
}

function createCachedResponse(req: HttpRequest<unknown>, body: unknown): HttpResponse<unknown> {
  return new HttpResponse({
    body,
    status: 200,
    statusText: 'OK',
    url: req.urlWithParams,
  });
}

function getResponseTtl(event: HttpResponse<unknown>, fallbackTtl: number): number {
  const cacheControl = event.headers.get('Cache-Control');
  const maxAge = cacheControl?.match(/max-age=(\d+)/i)?.[1];
  return maxAge ? Number(maxAge) * 1000 : fallbackTtl;
}

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  const cache = inject(HttpCacheService);
  const stats = inject(CacheStatsService);
  const config = getCacheConfig(req.url);
  const cacheKey = getCacheKey(req);

  const auth = req.headers.get('Authorization');
  const tokenSuffix = auth ? (auth.length > 20 ? auth.slice(-16) : auth) : '';
  const inFlightKey = `${req.method}:${req.urlWithParams}:${tokenSuffix}`;
  const existingRequest = inFlightRequests.get(inFlightKey);
  if (existingRequest) {
    return existingRequest as ReturnType<HttpInterceptorFn>;
  }

  const bypassHeaderNames = ['X-Cache-Bypass', 'x-cache-bypass', 'X-CACHE-BYPASS'];
  const hasBypassHeader = bypassHeaderNames.some((name) => req.headers.has(name));

  const isMutation = req.method !== 'GET';

  if (config.bypass || hasBypassHeader || isMutation) {
    const bypassRequest$ = next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          cache.set(
            cacheKey,
            event.body,
            getResponseTtl(event, config.ttl),
            event.headers.get('ETag') ?? undefined,
          );
        }
      }),
      finalize(() => inFlightRequests.delete(inFlightKey)),
      share(),
    );
    inFlightRequests.set(inFlightKey, bypassRequest$);
    return bypassRequest$ as ReturnType<HttpInterceptorFn>;
  }

  const cached = cache.getStaleWhileRevalidate(cacheKey, config.ttl, config.staleTtl ?? config.ttl);

  if (cached.data !== undefined) {
    if (cached.isStale) {
      stats.recordHit(true);
      revalidateInBackground(req, cacheKey, config, next, cache);
      return of(createCachedResponse(req, cached.data)) as ReturnType<HttpInterceptorFn>;
    } else {
      stats.recordHit(false);
      return of(createCachedResponse(req, cached.data)) as ReturnType<HttpInterceptorFn>;
    }
  }

  stats.recordMiss();

  const request$ = next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.status === 200) {
        cache.set(
          cacheKey,
          event.body,
          getResponseTtl(event, config.ttl),
          event.headers.get('ETag') ?? undefined,
        );
      }
    }),
    catchError((error: HttpErrorResponse) => {
      inFlightRequests.delete(inFlightKey);
      const staleData = cache.get(cacheKey);
      if (staleData) {
        return of(createCachedResponse(req, staleData.data)) as ReturnType<HttpInterceptorFn>;
      }
      throw error;
    }),
    finalize(() => inFlightRequests.delete(inFlightKey)),
    share(),
  );
  inFlightRequests.set(inFlightKey, request$);
  return request$ as ReturnType<HttpInterceptorFn>;
};

function revalidateInBackground(
  req: HttpRequest<unknown>,
  cacheKey: string,
  config: CacheConfig,
  next: HttpHandlerFn,
  cache: HttpCacheService,
): void {
  const timer = setTimeout(() => {
    backgroundTimers.delete(timer);
    next(req).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          cache.set(
            cacheKey,
            event.body,
            getResponseTtl(event, config.ttl),
            event.headers.get('ETag') ?? undefined,
          );
        }
      },
      error: () => {},
    });
  }, 0);
  backgroundTimers.add(timer);
}

export function clearBackgroundTimers(): void {
  backgroundTimers.forEach((timer) => clearTimeout(timer));
  backgroundTimers.clear();
}

export const optimisticUpdateInterceptor: HttpInterceptorFn = (req, next) => {
  const cache = inject(HttpCacheService);

  if (req.method === 'GET') {
    return next(req);
  }

  const invalidationPatterns = getInvalidationPatterns(req);

  for (const pattern of invalidationPatterns) {
    cache.invalidatePrefix(pattern);
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        for (const pattern of invalidationPatterns) {
          cache.invalidatePrefix(pattern);
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      throw error;
    }),
  ) as ReturnType<HttpInterceptorFn>;
};

function getInvalidationPatterns(req: HttpRequest<unknown>): string[] {
  const url = req.url;
  const patterns: string[] = [];

  if (url.includes('/brands')) {
    patterns.push('GET:/api/brands');
    patterns.push('GET:/api/discover/brands');
  }

  if (url.includes('/creators')) {
    patterns.push('GET:/api/creators');
    patterns.push('GET:/api/discover/creators');
  }

  if (url.includes('/listings')) {
    patterns.push('GET:/api/listings');
    patterns.push('GET:/api/discover/listings');
  }

  if (url.includes('/jobs') || url.includes('/job-posts')) {
    patterns.push('GET:/api/jobs/listing/paginated');
    patterns.push('GET:/api/job-posts');
  }

  if (url.includes('/profile')) {
    patterns.push('GET:/api/brands/profile');
    patterns.push('GET:/api/creators/profile');
  }

  if (url.includes('/connections')) {
    patterns.push('GET:/api/connections');
  }

  return patterns.length > 0 ? patterns : ['GET:/api/'];
}
